import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDownIcon, SearchIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { useCountries } from '../hooks/useUIConstants';
import InputLabel from './InputLabel';

interface CountrySelectorProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  errorMessage?: string;
  required?: boolean;
  useNaturalLettering?: boolean;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select a country...',
  label,
  errorMessage,
  required = false,
  useNaturalLettering = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { handPickedCountries, allCountries, loading, error } =
    useCountries(searchQuery);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const getCurrentCountry = () => {
    // If we have a value, return it directly - it doesn't matter which list it came from
    if (value) {
      return value;
    }
    return '';
  };

  const displayCountries = useMemo(() => {
    return searchQuery ? allCountries : handPickedCountries;
  }, [searchQuery, allCountries, handPickedCountries]);

  const currentCountry = getCurrentCountry();

  if (error) {
    console.error('Error loading countries:', error);
  }

  const handleCountrySelect = (country: string) => {
    onChange(country);
    setIsExpanded(false);
    setSearchQuery('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="space-y-2">
      {label && (
        <InputLabel label={label} useNaturalLettering={useNaturalLettering}>
          {required && <span className="text-red-100">*</span>}
        </InputLabel>
      )}

      <div className="relative" ref={dropdownRef}>
        <div
          className={classNames(
            'flex items-center justify-between rounded border-2 border-transparent bg-neutral-50 py-3 px-4 transition-colors',
            'text-neutral-150 focus:border-secondary-50 focus:outline-none focus:ring-0',
            'disabled:bg-transparent disabled:text-caption disabled:font-medium disabled:text-green-150',
            !disabled &&
              'cursor-pointer hover:border-secondary-50 hover:bg-neutral-75',
            errorMessage && 'border-red-100',
          )}
          onClick={() => !disabled && setIsExpanded(!isExpanded)}
        >
          <span
            className={
              currentCountry
                ? 'text-body text-neutral-150'
                : 'text-body text-neutral-125'
            }
          >
            {currentCountry || placeholder}
          </span>
          {!disabled && (
            <ChevronDownIcon
              className={classNames(
                'h-4 w-4 text-neutral-125 transition-transform',
                isExpanded && 'rotate-180',
              )}
            />
          )}
        </div>

        {isExpanded && !disabled && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-75 bg-neutral-50 shadow-400">
            {/* Search field */}
            <div className="border-b border-neutral-50 bg-neutral-50 p-4">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-125" />
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full rounded border border-neutral-75 bg-white py-2 pl-10 pr-3 text-small-caption shadow-sm focus:border-secondary-50 focus:outline-none"
                  autoFocus
                />
              </div>
            </div>

            {/* Country list */}
            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-neutral-125">
                  Loading countries...
                </div>
              ) : displayCountries.length > 0 ? (
                <div className="space-y-1 p-2">
                  {/* Show section header only when not searching and we have hand-picked countries */}
                  {!searchQuery && handPickedCountries.length > 0 && (
                    <div className="px-3 py-2">
                      <h4 className="font-sans text-small-caption text-neutral-125">
                        Popular Countries
                      </h4>
                    </div>
                  )}
                  {displayCountries.map((country) => (
                    <button
                      key={country}
                      onClick={() => handleCountrySelect(country)}
                      className={classNames(
                        'flex w-full items-center justify-between rounded px-3 py-2 text-left text-small transition-colors',
                        'hover:bg-neutral-50',
                        currentCountry === country &&
                          'bg-secondary-25 text-green-150',
                      )}
                    >
                      <span className="text-body text-neutral-150">
                        {country}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-neutral-125">
                  {searchQuery
                    ? 'No countries found'
                    : 'No countries available'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {errorMessage && (
        <p className="text-caption text-red-100">{errorMessage}</p>
      )}
    </div>
  );
};

export default CountrySelector;
